import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REQUEST_TYPES = [
  { value: 'acces', label: "Droit d'accès (obtenir mes données)" },
  { value: 'rectification', label: 'Droit de rectification (corriger mes données)' },
  { value: 'effacement', label: "Droit à l'effacement (droit à l'oubli)" },
  { value: 'portabilite', label: 'Droit à la portabilité (récupérer mes données)' },
  { value: 'opposition', label: "Droit d'opposition (arrêter un traitement)" },
  { value: 'limitation', label: 'Droit à la limitation du traitement' },
];

export default function ExercerDroitsRGPD() {
  const [form, setForm] = useState({ nom: '', email: '', type_demande: '', details: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [error, setError] = useState(null);
  const [requestId, setRequestId] = useState(null);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const canSubmit = form.nom.trim() && form.email.trim() && form.type_demande;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('loading');
    setError(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/rgpd/request`, form);
      setRequestId(data.request_id);
      setStatus('success');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Impossible d\'envoyer votre demande. Veuillez réessayer.');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b] text-[#dfddd6]" data-testid="page-exercer-droits">
      <Header />
      <main className="max-w-3xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <Link to="/politique-confidentialite" className="inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors mb-8 text-sm">
          <ArrowLeft size={16} /> Politique de confidentialité
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-[#d9fb06]/10 rounded-full flex items-center justify-center">
            <Shield className="text-[#d9fb06]" size={24} />
          </div>
          <h1 className="font-black text-4xl md:text-5xl text-[#d9fb06] uppercase">Exercer mes droits</h1>
        </div>
        <p className="text-[#888680] mb-10">
          Conformément au RGPD, vous pouvez à tout moment demander l'accès, la rectification, la suppression ou la portabilité de vos données personnelles. Nous vous répondrons sous <strong className="text-[#d9fb06]">1 mois</strong> maximum.
        </p>

        {status === 'success' ? (
          <div className="bg-[#302f2c] border-2 border-[#d9fb06] rounded-2xl p-8 text-center" data-testid="rgpd-success">
            <CheckCircle2 className="mx-auto text-[#d9fb06] mb-4" size={64} />
            <h2 className="text-2xl font-black text-[#d9fb06] uppercase mb-3">Demande enregistrée</h2>
            <p className="text-[#dfddd6] mb-2">
              Nous avons bien reçu votre demande. Un email de confirmation vient de vous être envoyé à <strong>{form.email}</strong>.
            </p>
            <p className="text-[#888680] text-sm mb-6">
              Numéro de suivi : <span className="font-mono text-[#d9fb06]">{requestId?.slice(0, 8)}</span>
            </p>
            <p className="text-[#888680] text-sm mb-6">
              Nous vous répondrons dans un délai maximal de <strong className="text-[#d9fb06]">1 mois</strong> (article 12 du RGPD).
            </p>
            <Link to="/">
              <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-8 py-3 text-sm font-bold uppercase">
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-[#302f2c] border border-[#3f4816] rounded-2xl p-6 md:p-8 space-y-5" data-testid="rgpd-form">
            <div>
              <label className="block text-sm font-bold text-[#d9fb06] mb-2 uppercase" htmlFor="rgpd-nom">Nom complet *</label>
              <input
                id="rgpd-nom"
                data-testid="rgpd-nom-input"
                type="text"
                required
                value={form.nom}
                onChange={handleChange('nom')}
                className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-3 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                placeholder="Jean Dupont"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#d9fb06] mb-2 uppercase" htmlFor="rgpd-email">Email *</label>
              <input
                id="rgpd-email"
                data-testid="rgpd-email-input"
                type="email"
                required
                value={form.email}
                onChange={handleChange('email')}
                className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-3 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                placeholder="jean.dupont@example.com"
              />
              <p className="text-xs text-[#888680] mt-1">L'email que vous nous avez communiqué (formulaire, newsletter, paiement...)</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-[#d9fb06] mb-2 uppercase" htmlFor="rgpd-type">Type de demande *</label>
              <select
                id="rgpd-type"
                data-testid="rgpd-type-select"
                required
                value={form.type_demande}
                onChange={handleChange('type_demande')}
                className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-3 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
              >
                <option value="">— Choisissez —</option>
                {REQUEST_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-[#d9fb06] mb-2 uppercase" htmlFor="rgpd-details">Détails (facultatif)</label>
              <textarea
                id="rgpd-details"
                data-testid="rgpd-details-textarea"
                value={form.details}
                onChange={handleChange('details')}
                rows={4}
                maxLength={2000}
                className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-3 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none resize-y"
                placeholder="Précisez votre demande si besoin (ex : rectification d'une info précise, portabilité vers un service tiers...)"
              />
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2 text-sm text-red-300" data-testid="rgpd-error">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="text-xs text-[#888680] leading-relaxed">
              En envoyant ce formulaire, vous consentez à ce que ces informations soient utilisées pour traiter votre demande. Elles ne seront pas utilisées à d'autres fins.
            </div>

            <Button
              type="submit"
              disabled={!canSubmit || status === 'loading'}
              data-testid="rgpd-submit-btn"
              className="w-full bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full py-3 font-bold uppercase disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === 'loading' ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Envoi en cours…</>
              ) : (
                'Envoyer ma demande'
              )}
            </Button>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
}
